import PropTypes from 'prop-types';
import Batch from '@cont/BatchGroup/Batch';
import SEO from '@seo/index';
import batchSchema from '@seo/batch/batch';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { fetchBatchFaq } from '@actions/batches';
import { fetchGroupBatchData, fetchTrialClasses } from '@actions/batchGroups';
import { wordsToSentence } from '@utils/textHelper';
import { PLATFORM_BATCH_PAGE } from '@constants/segment';
import setupInitialProps from '@utils/setupInitialProps';
import Layout from '@layout/FreeLayout/FreeLayout';
import { fetchPlatformBatchCourse } from '@api/fetchers/platformBatchGroup';
import { BatchType } from '@cont/BatchGroup/constants';
import escape from '@utils/escapeHTML';
import { getAccessToken } from '@selectors/auth';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const BatchGroupPage = ({
  batchGroupUID,
  batchUID,
  liveBatchCourses,
  recordedBatchCourses
}) => {
  const mapStateToProps = ({ batches, batchGroups }) => {
    return {
      batchDetails: batchGroups.batchDetails?.[batchGroupUID],
      trialClasses: batchGroups.trialClasses?.[batchUID],
      faqs: batches.batchFaqs?.faqs
    };
  };

  const props = useSelectorToStore(mapStateToProps);
  const { batchDetails = {}, schedule, trialClasses, faqs } = props;
  const { goal, name, languages, thumbnail } = batchDetails;
  const goalName = goal?.name || '';
  const metaTitle = `${goalName} - ${name} Offered by
  Unacademy`;
  const metaDescription = `${goalName} - ${name} is a
  course in ${wordsToSentence(
    languages,
    'label',
    ', ',
    ', '
  )} delivered by Top Educators. Join Unacademy to access the course.`;
  const canonicalURL = `https://unacademy.com/batch-group/${name}/${batchGroupUID}`;
  return (
    <>
      <SEO
        title={metaTitle}
        url={canonicalURL}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: thumbnail
            }
          ],
          url: canonicalURL
        }}
        schema={batchSchema({
          batch: { name, permalink: canonicalURL },
          batchDetails
        })}
      />
      <Batch
        batchDetails={batchDetails}
        schedule={schedule}
        trialClasses={trialClasses}
        faqs={faqs}
        liveBatchCourses={liveBatchCourses}
        recordedBatchCourses={recordedBatchCourses}
      />
    </>
  );
};

BatchGroupPage.pageInfo = {
  name: PLATFORM_BATCH_PAGE,
  isCMSMeta: true,
  isMWebSupported: true,
  layoutOptions: {
    hideSearch: true
  }
};

export default BatchGroupPage;

BatchGroupPage.getInitialProps = setupInitialProps(
  async ({ store, isServer, query }) => {
    const { batchGroupUID } = query;
    const promises = [];
    const date = new Date();
    const timezoneDifference = date.getTimezoneOffset() * -1;
    await store.dispatch(fetchGroupBatchData(batchGroupUID));
    const { batchUID, classType } =
      store.getState().batchGroups.data[batchGroupUID] || {};
    let liveBatchCourses = {};
    let recordedBatchCourses = {};
    const platformFaqId = `PLATFORM_${batchGroupUID}`;
    const state = store.getState();
    const accessToken = getAccessToken(state);
    if ([BatchType.liveBatch, BatchType.hybridBatch].includes(classType?.key)) {
      liveBatchCourses = await fetchPlatformBatchCourse({
        batchGroupUID,
        timezoneDifference,
        classType: BatchType.liveBatch,
        accessToken
      });
    }
    if (
      [BatchType.recordedBatch, BatchType.hybridBatch].includes(classType?.key)
    ) {
      recordedBatchCourses = await fetchPlatformBatchCourse({
        batchGroupUID,
        timezoneDifference,
        classType: BatchType.recordedBatch,
        accessToken
      });
    }

    promises.push(store.dispatch(fetchTrialClasses(batchUID)));
    promises.push(store.dispatch(fetchBatchFaq(platformFaqId)));
    if (promises.length) await Promise.all(promises);
    return {
      isServer,
      batchGroupUID: escape(batchGroupUID),
      batchUID,
      liveBatchCourses,
      recordedBatchCourses
    };
  },
  BatchGroupPage.pageInfo
);

BatchGroupPage.propTypes = {
  batchDetails: PropTypes.objectOf(PropTypes.any),
  batchGroupUID: PropTypes.string.isRequired,
  schedule: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  trialClasses: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  faqs: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
};

BatchGroupPage.defaultProps = {
  batchDetails: {},
  schedule: [],
  trialClasses: {},
  faqs: []
};
BatchGroupPage.Layout = Layout;
BatchGroupPage.Wrapper = PageReduxProvider;
